import React, { useEffect, useState } from 'react';

import Link from 'next/link';

import analytics from '@repo/analytics';
import { User } from '@repo/domain/user';
import { getElementAuthor } from '@repo/editor/src/components/Profile/utils';
import { AUTHENTICATION_CLICK_SIGNUP } from '@repo/editor/src/global/analytics/events';
import {
  useAuthenticationStore,
  authenticationStoreSelector,
} from '@repo/editor/src/stores/authenticationStore';
import {
  useUserStore,
  userStoreSelector,
} from '@repo/editor/src/stores/userStore';
import { timeAgo } from '@repo/editor/src/utils/timeAgo';
import { fireToast } from '@repo/ui/components/Toast';
import { H5, P3 } from '@repo/ui/components/Typography';
import { ProfileImage } from '@repo/ui/deprecated/ProfileImage';
import { BREAKPOINTS } from '@repo/ui/queries';
import { themeStyle } from '@repo/ui/theming';

import ReportModal from 'components/ReportModal/ReportModal';
import { buildPublicProfilePath } from 'global/urlUtils';
import userApi from 'global/userApi';
import { FlexRow } from 'styles/layouts';
import { LayoutSwitch } from 'styles/queries';

import Menu from '../Menu/Menu';

import { ProfileWrapper } from './styles';
import { ProfileRowProps } from './types';

export const ProfileRow = ({
  post,
  onClose,
  fromAuthorPage,
}: ProfileRowProps) => {
  const [showReportModal, setShowReportModal] = useState(false);

  const user = useUserStore(userStoreSelector.user);
  const openAuthentication = useAuthenticationStore(
    authenticationStoreSelector.openAuthentication
  );
  const updateUserInfo = useUserStore(userStoreSelector.updateUserInfo);
  const [authorData, setAuthorData] = useState<User | null>(null);

  const author = getElementAuthor({
    createdBy: {
      name: post.template.createdBy?.name ?? '',
      profileImage: post.template.createdBy?.profileImage ?? '',
    },
  });

  const onAuthorClick = (e: React.MouseEvent) => {
    if (!fromAuthorPage) {
      return;
    }
    e.preventDefault();
    onClose?.();
  };

  const [followStatus, setFollowStatus] = useState<{
    isLoading: boolean;
    isFollowing: boolean | null;
  }>({
    isLoading: false,
    isFollowing: null,
  });

  const handleToggleFollow = async () => {
    if (!user) {
      analytics.track(AUTHENTICATION_CLICK_SIGNUP, {
        label: 'followDesignAuthor',
      });
      openAuthentication();
      return;
    }
    if (!authorData) {
      return;
    }

    setFollowStatus((state) => ({ ...state, isLoading: true }));

    let response;

    if (followStatus.isFollowing) {
      response = await userApi.unfollow(authorData.slug);
    } else {
      response = await userApi.follow(authorData.slug);
    }

    if (response.success) {
      setFollowStatus((prev) => ({
        isLoading: false,
        isFollowing: !prev.isFollowing,
      }));
      fireToast({
        label: `You ${followStatus.isFollowing ? 'stopped' : 'started'} following ${author.name}`,
      });
      await updateUserInfo();
    }
  };

  useEffect(() => {
    const asyncSetAuthorData = async () => {
      if (post?.template?.createdBy?.slug) {
        const author: User | null = await userApi
          .getPublicUser(post?.template?.createdBy?.slug)
          .catch(() => null);
        if (author?.slug) {
          setAuthorData(author);
          setFollowStatus((prev) => ({
            ...prev,
            isFollowing: author.following,
          }));
        }
      }
    };

    if (post?.template?.createdBy?.slug) {
      void asyncSetAuthorData();
    }
  }, [post?.template?.createdBy?.slug]);

  const settingsMenuProps = {
    canFollow: user?.slug !== post.template.createdBy?.slug,
    handleFollow: handleToggleFollow,
    canReport: !!user,
    handleReport: () => setShowReportModal(true),
    followLabel: followStatus.isFollowing ? 'Unfollow' : 'Follow',
  };

  return (
    <FlexRow alignItems="center" justifyContent="space-between">
      <ProfileWrapper>
        <Link href={buildPublicProfilePath(post.template.createdBy?.slug)}>
          <ProfileImage profile={author} size="44px" onClick={onAuthorClick} />
        </Link>
        <div>
          <H5 as="h3">
            <Link
              href={buildPublicProfilePath(post.template.createdBy?.slug)}
              onClick={onAuthorClick}
            >
              {author.name}
            </Link>
          </H5>
          <P3 color={themeStyle.varInkMedium}>
            added a design {timeAgo(post.template.createdAt, true)}
          </P3>
        </div>
      </ProfileWrapper>
      <LayoutSwitch
        breakpoints={[BREAKPOINTS.largeTabletMin]}
        variationProps={[
          { props: { buttonHeight: '40px' } },
          { props: { buttonHeight: '30px' } },
        ]}
      >
        <Menu {...settingsMenuProps} align="end" buttonHeight="" />
      </LayoutSwitch>
      <ReportModal
        isOpen={showReportModal}
        referenceId={post?.id}
        type="POST"
        onClose={() => setShowReportModal(false)}
      />
    </FlexRow>
  );
};
